import { useQuery } from '@tanstack/react-query';
import { API } from '../api';
import { supabase } from '../lib/supabase';
import { queryKeys } from './queryKeys';
import { withCacheConfig } from './queryClient';
import { log } from '../utils/logger';

/**
 * React Query hooks for data fetching
 * Organized by domain for better maintainability and performance
 */

// ===== RESERVATIONS QUERIES =====
export const useReservations = (currentUser, filters = {}) => {
  return useQuery({
    queryKey: queryKeys.reservations.list(filters),
    queryFn: async () => {
      if (!currentUser) return [];

      log.info('🔍 React Query: Fetching reservations for user:', currentUser.id, currentUser.role);

      const filters = {
        dateFrom: new Date().toISOString(), // Only get future reservations
      };

      // For non-employees, filter by user
      if (currentUser.role !== 'teamrezy_employee') {
        filters.userId = currentUser.id;
      }

      const { data, error } = await API.reservations.getAll(filters);

      if (error) {
        log.error('❌ Reservation API error:', error);
        throw error;
      }

      log.info('✅ Fetched reservations via API:', data?.length || 0, 'for', currentUser.role);
      return data || [];
    },
    enabled: !!currentUser?.id,
    ...withCacheConfig('frequent')
  });
};

// Teams query for team manager or all teams for teamrezy_employee
export const useTeams = (currentUser) => {
  return useQuery({
    queryKey: ['teams', currentUser?.id, currentUser?.role],
    queryFn: async () => {
      if (!currentUser) return [];

      log.info('🔍 React Query: Fetching teams for user:', currentUser.id, currentUser.role);

      let data, error;
      
      // For teamrezy_employee, get all teams
      if (currentUser.role === 'teamrezy_employee') {
        ({ data, error } = await API.teams.getAll());
      } else {
        // For team managers, get their managed teams
        ({ data, error } = await API.teams.getByManager(currentUser.id));
      }

      if (error) {
        log.error('❌ Teams API error:', error);
        throw error;
      }

      log.info('✅ Fetched teams via API:', data?.length || 0, 'for', currentUser.role);
      return data || [];
    },
    enabled: !!currentUser?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Venues query
export const useVenues = (currentUser) => {
  return useQuery({
    queryKey: ['venues', currentUser?.id], // Include user ID to ensure cache invalidation per user
    queryFn: async () => {
      if (!currentUser?.id) {
        log.info('🔍 React Query: No currentUser, returning empty venues');
        return [];
      }
      
      // Verify Supabase session is available before making API call
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        log.error('❌ No valid Supabase session for venues query:', sessionError);
        throw new Error('Authentication session not available');
      }
      
      log.info('🔍 React Query: Fetching venues for authenticated user:', {
        userId: currentUser.id,
        userRole: currentUser.role,
        sessionEmail: session.user.email
      });

      const { data, error } = await API.venues.getAll();

      if (error) {
        log.error('❌ Venues API error:', error);
        throw error;
      }

      log.info('✅ Fetched venues via API:', data?.length || 0);
      return data || [];
    },
    enabled: !!currentUser?.id, // Only run when user is available
    ...withCacheConfig('stable'), // Use stable cache config for venues
  });
};

// Venue search query with filters
export const useVenueSearch = (currentUser, filters = {}, enabled = true) => {
  return useQuery({
    queryKey: ['venues', 'search', currentUser?.id, filters],
    queryFn: async () => {
      if (!currentUser?.id) {
        log.info('🔍 React Query: No currentUser for venue search, returning empty venues');
        return [];
      }
      
      // Verify Supabase session is available before making API call
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        log.error('❌ No valid Supabase session for venue search:', sessionError);
        throw new Error('Authentication session not available');
      }
      
      log.info('🔍 React Query: Searching venues with filters:', filters);

      const { data, error } = await API.venues.getAll(filters);

      if (error) {
        log.error('❌ Venue search API error:', error);
        throw error;
      }

      log.info('✅ Fetched venues via search API:', data?.length || 0, 'with filters:', filters);
      return data || [];
    },
    enabled: enabled && !!currentUser?.id, // Only run when enabled and user is available
    ...withCacheConfig('frequent'), // Use frequent cache config for search results
  });
};

// Tournaments query for teamrezy_employee
export const useTournaments = (currentUser) => {
  return useQuery({
    queryKey: ['tournaments', currentUser?.role],
    queryFn: async () => {
      if (!currentUser || currentUser.role !== 'teamrezy_employee') return [];

      log.info('🔍 React Query: Fetching tournaments for teamrezy_employee');

      const { data, error } = await API.tournaments.getAll();

      if (error) {
        log.error('❌ Tournaments API error:', error);
        throw error;
      }

      log.info('✅ Fetched tournaments via API:', data?.length || 0);
      return data || [];
    },
    enabled: currentUser?.role === 'teamrezy_employee',
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Pending team managers query for teamrezy_employee
export const usePendingTeamManagers = (currentUser) => {
  return useQuery({
    queryKey: ['pendingTeamManagers', currentUser?.role],
    queryFn: async () => {
      if (!currentUser || currentUser.role !== 'teamrezy_employee') return [];

      log.info('🔍 React Query: Fetching pending team managers for teamrezy_employee');

      // Since getPendingManagers doesn't exist in API, use direct Supabase call
      const { data, error } = await supabase
        .from('pending_team_managers')
        .select(`
          id, 
          user_id, 
          team_id, 
          status, 
          requested_role,
          requested_at,
          users!user_id (
            id, first_name, last_name, email, phone
          ),
          teams (
            id, name
          )
        `)
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      if (error) {
        log.error('❌ Pending team managers API error:', error);
        throw error;
      }

      log.info('✅ Fetched pending team managers via API:', data?.length || 0);
      return data || [];
    },
    enabled: currentUser?.role === 'teamrezy_employee',
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Dashboard data aggregator hook - combines all data needed for dashboard
export const useDashboardData = (currentUser, _options = {}) => {
  // Options available for future extension

  // Fetch venues and reservations based on options
  const venuesQuery = useVenues(currentUser);
  const reservationsQuery = useReservations(currentUser);

  // Return combined data object compatible with existing App.js usage
  return {
    data: {
      venues: venuesQuery.data || [],
      reservations: reservationsQuery.data || []
    },
    loading: {
      venues: venuesQuery.isLoading,
      reservations: reservationsQuery.isLoading,
      any: venuesQuery.isLoading || reservationsQuery.isLoading
    },
    errors: {
      venues: venuesQuery.error,
      reservations: reservationsQuery.error,
      any: venuesQuery.error || reservationsQuery.error
    },
    isReady: !venuesQuery.isLoading && !reservationsQuery.isLoading,
    hasData: (venuesQuery.data?.length > 0) || (reservationsQuery.data?.length > 0),
    actions: {
      refreshAll: async () => {
        await Promise.all([
          venuesQuery.refetch(),
          reservationsQuery.refetch()
        ]);
        return { success: true };
      },
      clearAllCache: () => {
        // This would be handled by React Query's cache invalidation
        log.info('Cache clearing handled by React Query');
      }
    },
    selectors: {
      getOverviewStats: () => ({
        reservations: reservationsQuery.data?.length || 0,
        venues: venuesQuery.data?.length || 0
      }),
      getRecentActivity: () => [],
      getUpcomingEvents: () => [],
      getDashboardMetrics: () => ({
        reservations: {
          total: reservationsQuery.data?.length || 0,
          upcomingCount: reservationsQuery.data?.filter(r => 
            new Date(r.reserved_at) > new Date()
          ).length || 0
        },
        venues: {
          total: venuesQuery.data?.length || 0,
          active: venuesQuery.data?.length || 0
        }
      }),
      searchAll: (query) => {
        if (!query?.trim()) return { venues: [], reservations: [] };
        const searchTerm = query.toLowerCase();
        return {
          venues: (venuesQuery.data || []).filter(venue =>
            venue.name?.toLowerCase().includes(searchTerm) ||
            venue.city?.toLowerCase().includes(searchTerm)
          ),
          reservations: (reservationsQuery.data || []).filter(reservation =>
            reservation.venues?.name?.toLowerCase().includes(searchTerm) ||
            reservation.teams?.name?.toLowerCase().includes(searchTerm)
          )
        };
      }
    }
  };
};

// ===== CONTACT QUERIES =====

// Contact messages query for TeamRezy employees
export const useContactMessages = (currentUser, filters = {}) => {
  return useQuery({
    queryKey: queryKeys.contact.list(filters),
    queryFn: async () => {
      if (!currentUser || currentUser.role !== 'teamrezy_employee') return [];

      log.info('🔍 React Query: Fetching contact messages for teamrezy_employee');

      const { data, error } = await API.contact.getAll(filters);

      if (error) {
        log.error('❌ Contact messages API error:', error);
        throw error;
      }

      log.info('✅ Fetched contact messages via API:', data?.length || 0);
      return data || [];
    },
    enabled: currentUser?.role === 'teamrezy_employee',
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...withCacheConfig('frequent')
  });
};

// Contact messages by user query
export const useUserContactMessages = (currentUser) => {
  return useQuery({
    queryKey: queryKeys.contact.byUser(currentUser?.id),
    queryFn: async () => {
      if (!currentUser?.id) return [];

      log.info('🔍 React Query: Fetching contact messages for user:', currentUser.id);

      const { data, error } = await API.contact.getByUser(currentUser.id);

      if (error) {
        log.error('❌ User contact messages API error:', error);
        throw error;
      }

      log.info('✅ Fetched user contact messages via API:', data?.length || 0);
      return data || [];
    },
    enabled: !!currentUser?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...withCacheConfig('standard')
  });
};

// Contact message detail query
export const useContactMessage = (messageId, currentUser) => {
  return useQuery({
    queryKey: queryKeys.contact.detail(messageId),
    queryFn: async () => {
      if (!messageId) return null;

      log.info('🔍 React Query: Fetching contact message:', messageId);

      const { data, error } = await API.contact.getById(messageId);

      if (error) {
        log.error('❌ Contact message API error:', error);
        throw error;
      }

      log.info('✅ Fetched contact message via API');
      return data;
    },
    enabled: !!messageId && (
      currentUser?.role === 'teamrezy_employee' || 
      (currentUser?.id && currentUser?.id === messageId) // Users can view their own messages
    ),
    staleTime: 10 * 60 * 1000, // 10 minutes (contact messages don't change often)
    ...withCacheConfig('standard')
  });
};

// Contact statistics query for admin dashboard
export const useContactStats = (currentUser) => {
  return useQuery({
    queryKey: queryKeys.contact.stats(),
    queryFn: async () => {
      if (!currentUser || currentUser.role !== 'teamrezy_employee') return null;

      log.info('🔍 React Query: Fetching contact statistics');

      const { data, error } = await API.contact.getStats();

      if (error) {
        log.error('❌ Contact statistics API error:', error);
        throw error;
      }

      log.info('✅ Fetched contact statistics via API');
      return data;
    },
    enabled: currentUser?.role === 'teamrezy_employee',
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...withCacheConfig('standard')
  });
};
